import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, notification } from 'antd';
import styled from 'styled-components';
import logo from '../../assets/img/logo-v2.png';
import src from '../../assets/img/bg.svg';
import { config } from '../../config/env-config';
import { InvalidMcpAuthorization, McpAuthorization } from '../../core/modules/auth/mcp-authorization';
import { useMcpGrant } from '../../core/modules/auth/hooks/useMcpGrant.query';
import { useUser } from '../../core/modules/user/hooks';

const Wrapper = styled.div`
  background: rgb(24, 144, 255);
  background: url(${src}) no-repeat fixed;
  height: 100vh;
  padding: 15px;
  background-size: cover;
`;

const Header = styled.header`
  .auth-logo {
    max-width: 165px;
  }
`;

const Content = styled.main`
  height: 60vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 500px;
  background-color: white;
  padding: 48px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  h2 {
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    letter-spacing: 0.05em;
    color: #000000;
  }
  p {
    font-size: 16px;
    line-height: 22px;
    margin-bottom: 16px;
  }
  .actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
  }
`;

const ProblemMessage = styled.p`
  color: red;
`;

const SIGNED_IN_HINT = 'Signed in as';

const readAuthorization = (search: string) => {
  try {
    return { authorization: new McpAuthorization(config.REACT_APP_MCP_CALLBACK_URL, search) };
  } catch (error) {
    if (error instanceof InvalidMcpAuthorization) {
      return { problem: error.message };
    }

    throw error;
  }
};

interface IMcpAuthorizePageProps {
  navigate?: (url: string) => void;
}

const McpAuthorizePage: React.FC<IMcpAuthorizePageProps> = ({
  navigate = (url: string) => window.location.assign(url)
}) => {
  const { search } = useLocation();
  const { data: user } = useUser();
  const { mutate, isLoading } = useMcpGrant();
  const { authorization, problem } = useMemo(() => readAuthorization(search), [search]);

  // The consent screen is a one-off. Clear the saved return path so a later sign-in
  // does not land on an old request.
  const leave = (url: string) => {
    localStorage.removeItem('redirectPath');
    navigate(url);
  };

  const allow = () =>
    mutate(authorization.requestId, {
      onSuccess: (grant) => leave(authorization.grantedUrl(grant)),
      onError: () =>
        notification.error({
          message: 'Access was not granted.',
          description: 'Try again. If it keeps failing, start the connection again from the app.'
        })
    });

  return (
    <Wrapper>
      <Header>
        <img className='auth-logo' src={logo} alt='logo' />
      </Header>
      <Content>
        <Card>
          <h2>AUTHORIZE APP ACCESS</h2>
          {problem ? (
            <ProblemMessage>{problem}</ProblemMessage>
          ) : (
            <>
              {user?.email && <p>{SIGNED_IN_HINT} {user.email}</p>}
              <p>
                An app you connected is asking for read-only access to Brenda title data on your behalf.
                It cannot change anything in Brenda. Access ends if your Brenda account is deactivated.
              </p>
              <div className='actions'>
                <Button type='primary' loading={isLoading} onClick={allow}>
                  Allow
                </Button>
                <Button disabled={isLoading} onClick={() => leave(authorization.deniedUrl())}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </Card>
      </Content>
    </Wrapper>
  );
};

export default McpAuthorizePage;
